import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Plus, Trash2, Pencil, X, MapPin, Clock, Store, Copy, Check, GripVertical, StickyNote, Bell } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { PLATFORMS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "mt-2 w-full border-2 border-[#1A1A1A] bg-white px-4 py-3 text-sm font-medium text-[#1A1A1A] outline-none focus:shadow-[3px_3px_0_0_#E63946]";

export default function PanelPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [jobApps, setJobApps] = useState([]);
  const [franchiseApps, setFranchiseApps] = useState([]);
  const [tab, setTab] = useState("branches");
  const [branchQuery, setBranchQuery] = useState("");
  const dragIndex = useRef(null);
  const [jobQuery, setJobQuery] = useState("");
  const [jobPos, setJobPos] = useState("");
  const [frQuery, setFrQuery] = useState("");
  const [frCity, setFrCity] = useState("");
  const [editApp, setEditApp] = useState(null); // { kind: "job" | "franchise", id }
  const [appForm, setAppForm] = useState({});
  const [noteApp, setNoteApp] = useState(null); // { kind, id }
  const [noteText, setNoteText] = useState("");
  const [form, setForm] = useState({ code: "", name: "", address: "", hours: "", gelal_url: "", payment_url: "", yemeksepeti_url: "", getir_url: "", trendyol_url: "", migros_url: "" });
  const [editingId, setEditingId] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [loading, setLoading] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/giris");
  }, [user, navigate]);

  const fetchBranches = useCallback(() => {
    axios
      .get(`${API}/branches`)
      .then((res) => setBranches(res.data))
      .catch(() => {});
  }, []);

  const byNewest = (a, b) => new Date(b.created_at) - new Date(a.created_at);

  const fetchApplications = useCallback(() => {
    axios
      .get(`${API}/applications`, { withCredentials: true })
      .then((res) => setJobApps([...res.data].sort(byNewest)))
      .catch(() => {});
    axios
      .get(`${API}/franchise-applications`, { withCredentials: true })
      .then((res) => setFranchiseApps([...res.data].sort(byNewest)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      fetchBranches();
      fetchApplications();
    }
  }, [user, fetchBranches, fetchApplications]);

  useEffect(() => {
    if (!user) return;
    const t = setInterval(fetchApplications, 30000);
    return () => clearInterval(t);
  }, [user, fetchApplications]);

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7]">
        <p className="text-sm font-bold uppercase tracking-wide text-[#4A4A4A]">
          Yükleniyor...
        </p>
      </div>
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const resetForm = () => {
    setForm({ code: "", name: "", address: "", hours: "", gelal_url: "", payment_url: "", yemeksepeti_url: "", getir_url: "", trendyol_url: "", migros_url: "" });
    setEditingId(null);
  };

  const handleEdit = (branch) => {
    setEditingId(branch.id);
    setForm({
      code: branch.code,
      name: branch.name,
      address: branch.address,
      hours: branch.hours,
      gelal_url: branch.gelal_url || "",
      payment_url: branch.payment_url || "",
      yemeksepeti_url: branch.yemeksepeti_url || "",
      getir_url: branch.getir_url || "",
      trendyol_url: branch.trendyol_url || "",
      migros_url: branch.migros_url || "",
    });
    setStatus({ type: "", msg: "" });
  };

  const copyGelAlLink = (code) => {
    const url = `${window.location.origin}/gel-al?sube=${code}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });
    setLoading(true);
    try {
      if (editingId) {
        await axios.put(`${API}/branches/${editingId}`, form, { withCredentials: true });
        setStatus({ type: "success", msg: "Şube güncellendi — sitede yayında." });
      } else {
        await axios.post(`${API}/branches`, form, { withCredentials: true });
        setStatus({ type: "success", msg: "Şube eklendi — sitede yayında." });
      }
      resetForm();
      fetchBranches();
    } catch (err) {
      setStatus({
        type: "error",
        msg: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (branch) => {
    try {
      await axios.delete(`${API}/branches/${branch.id}`, { withCredentials: true });
      fetchBranches();
    } catch {
      setStatus({ type: "error", msg: "Şube silinemedi." });
    }
  };

  const handleDrop = async (targetId) => {
    const fromId = dragIndex.current;
    dragIndex.current = null;
    if (!fromId || fromId === targetId) return;
    const next = [...branches];
    const from = next.findIndex((b) => b.id === fromId);
    const to = next.findIndex((b) => b.id === targetId);
    if (from === -1 || to === -1) return;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setBranches(next);
    try {
      await axios.post(`${API}/branches/reorder`, { ids: next.map((b) => b.id) }, { withCredentials: true });
    } catch {
      setStatus({ type: "error", msg: "Sıralama kaydedilemedi." });
      fetchBranches();
    }
  };

  const filteredBranches = branches.filter(
    (b) =>
      !branchQuery.trim() ||
      `${b.code} ${b.name} ${b.address}`.toLowerCase().includes(branchQuery.trim().toLowerCase())
  );

  const jobPositions = [...new Set(jobApps.map((a) => a.position))];
  const filteredJobs = jobApps.filter(
    (a) =>
      (!jobPos || a.position === jobPos) &&
      (!jobQuery.trim() || `${a.name} ${a.phone} ${a.note || ""}`.toLowerCase().includes(jobQuery.trim().toLowerCase()))
  );
  const frCities = [...new Set(franchiseApps.map((a) => a.city))];
  const filteredFr = franchiseApps.filter(
    (a) =>
      (!frCity || a.city === frCity) &&
      (!frQuery.trim() || `${a.name} ${a.phone} ${a.email} ${a.city}`.toLowerCase().includes(frQuery.trim().toLowerCase()))
  );

  const appEndpoint = { job: "applications", franchise: "franchise-applications" };

  const unreadJobs = jobApps.filter((a) => !a.is_read);
  const unreadFr = franchiseApps.filter((a) => !a.is_read);
  const unreadCount = unreadJobs.length + unreadFr.length;

  const markReadAndGo = async (kind, a) => {
    setNotifOpen(false);
    setTab(kind === "job" ? "jobs" : "franchise");
    try {
      await axios.put(`${API}/${appEndpoint[kind]}/${a.id}`, { is_read: true }, { withCredentials: true });
      fetchApplications();
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      fetchApplications();
    } catch {}
  };

  const startEditApp = (kind, a) => {
    setEditApp({ kind, id: a.id });
    setNoteApp(null);
    setAppForm(
      kind === "job"
        ? { name: a.name, phone: a.phone, position: a.position }
        : { name: a.name, phone: a.phone, email: a.email, city: a.city }
    );
  };

  const saveEditApp = async () => {
    try {
      await axios.put(`${API}/${appEndpoint[editApp.kind]}/${editApp.id}`, appForm, { withCredentials: true });
      setEditApp(null);
      fetchApplications();
    } catch (err) {
      setStatus({ type: "error", msg: formatApiErrorDetail(err.response?.data?.detail) || "Başvuru güncellenemedi." });
    }
  };

  const startNote = (kind, a) => {
    setNoteApp({ kind, id: a.id });
    setEditApp(null);
    setNoteText(a.admin_note || "");
  };

  const saveNote = async () => {
    try {
      await axios.put(`${API}/${appEndpoint[noteApp.kind]}/${noteApp.id}`, { admin_note: noteText }, { withCredentials: true });
      setNoteApp(null);
      fetchApplications();
    } catch {
      setStatus({ type: "error", msg: "Not kaydedilemedi." });
    }
  };

  const deleteApp = async (kind, a) => {
    if (!window.confirm(`${a.name} adlı kişinin başvurusu silinsin mi?`)) return;
    try {
      await axios.delete(`${API}/${appEndpoint[kind]}/${a.id}`, { withCredentials: true });
      fetchApplications();
    } catch {
      setStatus({ type: "error", msg: "Başvuru silinemedi." });
    }
  };

  const appActionBtn = "btn-chunky bg-white p-2";
  const appButtons = (kind, a) => (
    <div className="flex shrink-0 gap-2">
      <button
        type="button"
        data-testid={`${kind}-app-edit-${a.id}`}
        onClick={() => startEditApp(kind, a)}
        className={`${appActionBtn} text-[#1A1A1A]`}
        aria-label={`${a.name} başvurusunu düzenle`}
      >
        <Pencil size={16} strokeWidth={2.5} />
      </button>
      <button
        type="button"
        data-testid={`${kind}-app-note-${a.id}`}
        onClick={() => startNote(kind, a)}
        className={`${appActionBtn} ${a.admin_note ? "text-[#E8A13A]" : "text-[#1A1A1A]"}`}
        aria-label={`${a.name} başvurusuna not al`}
      >
        <StickyNote size={16} strokeWidth={2.5} />
      </button>
      <button
        type="button"
        data-testid={`${kind}-app-delete-${a.id}`}
        onClick={() => deleteApp(kind, a)}
        className={`${appActionBtn} text-[#E63946]`}
        aria-label={`${a.name} başvurusunu sil`}
      >
        <Trash2 size={16} strokeWidth={2.5} />
      </button>
    </div>
  );

  const noteEditor = (kind, a) =>
    noteApp?.kind === kind && noteApp.id === a.id ? (
      <div className="mt-3 border-t border-dashed border-[#1A1A1A] pt-3">
        <textarea
          data-testid={`${kind}-app-note-input`}
          rows={2}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          className={inputCls}
          placeholder="Bu başvuruyla ilgili iç notunuz (başvuran görmez)..."
        />
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            data-testid={`${kind}-app-note-save`}
            onClick={saveNote}
            className="btn-chunky bg-[#1A1A1A] px-4 py-2 text-xs uppercase tracking-wide text-white"
          >
            Notu Kaydet
          </button>
          <button
            type="button"
            data-testid={`${kind}-app-note-cancel`}
            onClick={() => setNoteApp(null)}
            className="btn-chunky bg-white px-4 py-2 text-xs uppercase tracking-wide text-[#1A1A1A]"
          >
            Vazgeç
          </button>
        </div>
      </div>
    ) : null;

  const TABS = [
    { key: "branches", label: "Şubeler", count: branches.length },
    { key: "jobs", label: "İş Başvuruları", count: jobApps.length },
    { key: "franchise", label: "Bayilik Başvuruları", count: franchiseApps.length },
  ];

  return (
    <div data-testid="panel-page" className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b-2 border-[#1A1A1A]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
          <img
            src="/assets/logo.png"
            alt="Deny's Burger & Fries logosu"
            className="h-12 w-auto"
          />
          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                data-testid="panel-notifications-button"
                onClick={() => setNotifOpen((o) => !o)}
                className="btn-chunky relative flex items-center bg-white p-2.5 text-[#1A1A1A]"
                aria-label="Bildirimler"
              >
                <Bell size={16} strokeWidth={2.5} />
                {unreadCount > 0 && (
                  <span
                    data-testid="panel-notifications-badge"
                    className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center border-2 border-[#1A1A1A] bg-[#E63946] px-1 text-[10px] font-black text-white"
                  >
                    {unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setNotifOpen(false)} />
                  <div
                    data-testid="panel-notifications-dropdown"
                    className="absolute right-0 z-20 mt-2 w-80 border-2 border-[#1A1A1A] bg-white shadow-[4px_4px_0_0_#1A1A1A]"
                  >
                    <p className="border-b-2 border-[#1A1A1A] px-4 py-2 text-xs font-black uppercase tracking-widest text-[#1A1A1A]">
                      Bildirimler ({unreadCount})
                    </p>
                    {unreadCount === 0 ? (
                      <p data-testid="notifications-empty" className="px-4 py-4 text-sm font-medium text-[#4A4A4A]">
                        Yeni bildirim yok.
                      </p>
                    ) : (
                      <div className="max-h-80 overflow-y-auto">
                        {[
                          ...unreadJobs.map((a) => ({ kind: "job", a })),
                          ...unreadFr.map((a) => ({ kind: "franchise", a })),
                        ]
                          .sort((x, y) => new Date(y.a.created_at) - new Date(x.a.created_at))
                          .map(({ kind, a }) => (
                            <button
                              key={`${kind}-${a.id}`}
                              data-testid={`notification-item-${kind}-${a.id}`}
                              onClick={() => markReadAndGo(kind, a)}
                              className="block w-full border-b border-dashed border-[#1A1A1A] px-4 py-3 text-left transition-colors hover:bg-[#FDFBF7]"
                            >
                              <span
                                className={`inline-block border px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest ${
                                  kind === "job"
                                    ? "border-[#1A1A1A] text-[#1A1A1A]"
                                    : "border-[#E63946] text-[#E63946]"
                                }`}
                              >
                                {kind === "job" ? "İş Başvurusu" : "Bayilik Başvurusu"}
                              </span>
                              <span className="mt-1 block text-sm font-black text-[#1A1A1A]">{a.name}</span>
                              <span className="block text-xs font-medium text-[#4A4A4A]">
                                {new Date(a.created_at).toLocaleString("tr-TR")}
                              </span>
                            </button>
                          ))}
                      </div>
                    )}
                    {unreadCount > 0 && (
                      <button
                        data-testid="notifications-mark-all-read"
                        onClick={markAllRead}
                        className="w-full bg-[#1A1A1A] px-4 py-2 text-xs font-black uppercase tracking-widest text-white"
                      >
                        Tümünü Okundu Say
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            <button
              data-testid="panel-logout-button"
              onClick={handleLogout}
              className="btn-chunky flex items-center gap-2 bg-white px-4 py-2 text-sm uppercase tracking-wide text-[#1A1A1A]"
            >
              <LogOut size={16} strokeWidth={2.5} /> Çıkış
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-12 md:px-8">
        <p className="font-script text-2xl text-[#E63946]">Yönetim Paneli</p>
        <h1 className="mt-2 text-3xl font-black uppercase tracking-tighter text-[#1A1A1A] sm:text-4xl">
          {tab === "branches" ? "Şube Yönetimi" : tab === "jobs" ? "İş Başvuruları" : "Bayilik Başvuruları"}
        </h1>

        <div className="mt-8 flex flex-wrap gap-3" data-testid="panel-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              data-testid={`panel-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`btn-chunky px-5 py-2.5 text-sm uppercase tracking-wide ${
                tab === t.key ? "bg-[#1A1A1A] text-white" : "bg-white text-[#1A1A1A]"
              }`}
            >
              {t.label} ({t.count})
            </button>
          ))}
        </div>

        {tab === "branches" && (
        <>
        <p className="mt-6 text-sm font-medium text-[#4A4A4A]">
          Buradan eklediğiniz şubeler anında sitenin "Şubeler" bölümünde yayınlanır.
        </p>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-2">
          <form onSubmit={handleAdd} data-testid="branch-form" className="card-print h-fit p-6 md:p-8">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-lg font-black uppercase tracking-tight text-[#1A1A1A]">
                {editingId ? "Şubeyi Düzenle" : "Yeni Şube Ekle"}
              </h2>
              {editingId && (
                <button
                  type="button"
                  data-testid="branch-cancel-edit-button"
                  onClick={resetForm}
                  className="flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-[#4A4A4A] transition-colors hover:text-[#E63946]"
                >
                  <X size={14} strokeWidth={2.5} /> Vazgeç
                </button>
              )}
            </div>
            <div className="mt-6 space-y-5">
              <div>
                <label htmlFor="branch-code" className="text-xs font-bold uppercase tracking-wide text-[#1A1A1A]">
                  Şube Kodu
                </label>
                <input
                  id="branch-code"
                  data-testid="branch-code-input"
                  required
                  value={form.code}
                  onChange={handleChange("code")}
                  className={inputCls}
                  placeholder="Örn: ATAKUM2"
                />
              </div>
              <div>
                <label htmlFor="branch-name" className="text-xs font-bold uppercase tracking-wide text-[#1A1A1A]">
                  Şube Adı
                </label>
                <input
                  id="branch-name"
                  data-testid="branch-name-input"
                  required
                  value={form.name}
                  onChange={handleChange("name")}
                  className={inputCls}
                  placeholder="Örn: Deny's Burger & Fries — Atakum"
                />
              </div>
              <div>
                <label htmlFor="branch-address" className="text-xs font-bold uppercase tracking-wide text-[#1A1A1A]">
                  Adres
                </label>
                <input
                  id="branch-address"
                  data-testid="branch-address-input"
                  required
                  value={form.address}
                  onChange={handleChange("address")}
                  className={inputCls}
                  placeholder="Sokak, Mahalle, No, İlçe / İl"
                />
              </div>
              <div>
                <label htmlFor="branch-hours" className="text-xs font-bold uppercase tracking-wide text-[#1A1A1A]">
                  Çalışma Saatleri
                </label>
                <input
                  id="branch-hours"
                  data-testid="branch-hours-input"
                  required
                  value={form.hours}
                  onChange={handleChange("hours")}
                  className={inputCls}
                  placeholder="Örn: Her gün 11:00 - 22:00"
                />
              </div>
              <div>
                <label htmlFor="branch-gelal-url" className="text-xs font-bold uppercase tracking-wide text-[#1A1A1A]">
                  Gel Al Linki (isteğe bağlı)
                </label>
                <input
                  id="branch-gelal-url"
                  data-testid="branch-gelal-url-input"
                  value={form.gelal_url}
                  onChange={handleChange("gelal_url")}
                  className={inputCls}
                  placeholder="Boş bırakırsanız otomatik site içi link kullanılır"
                />
                <p className="mt-1 text-xs font-medium text-[#4A4A4A]">
                  Şubenin kendi sipariş sayfası/linki varsa buraya girin; boşsa "Gel Al" butonu sitenin sipariş sayfasına gider.
                </p>
              </div>
              <div>
                <label htmlFor="branch-payment-url" className="text-xs font-bold uppercase tracking-wide text-[#1A1A1A]">
                  Ödeme Linki — iyzico (isteğe bağlı)
                </label>
                <input
                  id="branch-payment-url"
                  data-testid="branch-payment-url-input"
                  value={form.payment_url}
                  onChange={handleChange("payment_url")}
                  className={inputCls}
                  placeholder="https://iyzi.link/..."
                />
                <p className="mt-1 text-xs font-medium text-[#4A4A4A]">
                  iyzico ödeme linkini buraya yapıştırın — müşteri Gel Al sayfasında "Kredi Kartıyla Öde" butonu görür.
                </p>
              </div>

              <div className="border-2 border-dashed border-[#1A1A1A] bg-[#FDFBF7] p-4">
                <p className="text-xs font-black uppercase tracking-wide text-[#1A1A1A]">
                  Yemek Platformu Linkleri (isteğe bağlı)
                </p>
                <p className="mt-1 text-xs font-medium text-[#4A4A4A]">
                  Linkini girdiğiniz platformun logosu bu şubenin kartında görünür; müşteri tıklayınca platformdaki restoran sayfanız açılır.
                </p>
                <div className="mt-3 space-y-3">
                  {PLATFORMS.map((p) => (
                    <div key={p.key} className="flex items-center gap-3">
                      <img src={p.logo} alt={`${p.name} logosu`} className="h-5 w-14 shrink-0 object-contain object-left" loading="lazy" />
                      <input
                        data-testid={`branch-platform-${p.key}`}
                        type="url"
                        value={form[p.key]}
                        onChange={handleChange(p.key)}
                        className={inputCls}
                        placeholder={`${p.name} restoran linki (https://...)`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {status.msg && (
                <div
                  data-testid={status.type === "success" ? "branch-success-message" : "branch-error-message"}
                  className={`border-2 px-4 py-3 text-sm font-bold ${
                    status.type === "success"
                      ? "border-[#1A7A3D] bg-[#1A7A3D]/10 text-[#1A7A3D]"
                      : "border-[#E63946] bg-[#E63946]/10 text-[#E63946]"
                  }`}
                >
                  {status.msg}
                </div>
              )}

              <button
                data-testid="branch-add-button"
                type="submit"
                disabled={loading}
                className="btn-chunky w-full justify-center bg-[#E63946] px-6 py-3 text-base uppercase tracking-wide text-white disabled:opacity-60"
              >
                <Plus size={18} strokeWidth={2.5} />
                {loading
                  ? editingId
                    ? "Kaydediliyor..."
                    : "Ekleniyor..."
                  : editingId
                    ? "Değişiklikleri Kaydet"
                    : "Şubeyi Yayınla"}
              </button>
            </div>
          </form>

          <div>
            <h2 className="text-lg font-black uppercase tracking-tight text-[#1A1A1A]">
              Yayınlanan Şubeler ({branches.length})
            </h2>
            <p className="mt-1 text-xs font-medium text-[#4A4A4A]">
              Sıralamayı değiştirmek için kartı tutup sürükleyin — sıra sitede de aynen görünür.
            </p>
            <input
              data-testid="panel-branch-search"
              type="text"
              value={branchQuery}
              onChange={(e) => setBranchQuery(e.target.value)}
              className="mt-4 w-full max-w-sm border-2 border-[#1A1A1A] bg-white px-4 py-2.5 text-sm font-medium text-[#1A1A1A] outline-none focus:shadow-[3px_3px_0_0_#E63946]"
              placeholder="Şube ara (kod, ad veya adres)..."
            />
            <div className="mt-6 space-y-4" data-testid="branch-list">
              {filteredBranches.length === 0 && (
                <p className="text-sm font-medium text-[#4A4A4A]">Aramayla eşleşen şube bulunamadı.</p>
              )}
              {filteredBranches.map((b) => (
                <div
                  key={b.id}
                  data-testid={`panel-branch-${b.code.toLowerCase()}`}
                  className="card-print flex items-start justify-between gap-4 p-5"
                  draggable
                  onDragStart={() => { dragIndex.current = b.id; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(b.id)}
                >
                  <GripVertical size={18} strokeWidth={2.5} className="mt-1 shrink-0 cursor-grab text-gray-400" data-testid={`branch-drag-handle-${b.code.toLowerCase()}`} />
                  <div>
                    <p className="flex items-center gap-2 text-base font-black uppercase tracking-tight text-[#1A1A1A]">
                      <Store size={16} strokeWidth={2.5} className="text-[#E63946]" />
                      {b.name}
                      <span className="border border-[#1A1A1A] px-2 py-0.5 text-[10px] font-black tracking-widest">
                        {b.code}
                      </span>
                      {b.payment_ready && (
                        <span
                          data-testid={`branch-payment-badge-${b.code.toLowerCase()}`}
                          className="border border-[#1A7A3D] bg-[#1A7A3D]/10 px-2 py-0.5 text-[10px] font-black tracking-widest text-[#1A7A3D]"
                        >
                          Online Ödeme Aktif
                        </span>
                      )}
                    </p>
                    <p className="mt-2 flex items-start gap-2 text-xs font-medium text-[#4A4A4A]">
                      <MapPin size={13} strokeWidth={2.5} className="mt-0.5 shrink-0" /> {b.address}
                    </p>
                    <p className="mt-1 flex items-start gap-2 text-xs font-medium text-[#4A4A4A]">
                      <Clock size={13} strokeWidth={2.5} className="mt-0.5 shrink-0" /> {b.hours}
                    </p>
                    <div className="mt-3 flex items-center gap-2 border border-dashed border-[#1A1A1A] bg-[#FDFBF7] px-2 py-1.5">
                      <span className="flex-1 truncate text-[11px] font-medium text-[#4A4A4A]">
                        {b.gelal_url || `${window.location.origin}/gel-al?sube=${b.code}`}
                      </span>
                      <button
                        data-testid={`branch-copy-gelal-${b.code.toLowerCase()}`}
                        onClick={() => copyGelAlLink(b.code)}
                        className="shrink-0 text-[#1A1A1A] transition-colors hover:text-[#E63946]"
                        aria-label="Gel Al linkini kopyala"
                      >
                        {copiedCode === b.code ? (
                          <Check size={14} strokeWidth={2.5} className="text-[#1A7A3D]" />
                        ) : (
                          <Copy size={14} strokeWidth={2.5} />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      data-testid={`branch-edit-${b.code.toLowerCase()}`}
                      onClick={() => handleEdit(b)}
                      className="btn-chunky bg-white p-2 text-[#1A1A1A]"
                      aria-label={`${b.name} şubesini düzenle`}
                    >
                      <Pencil size={16} strokeWidth={2.5} />
                    </button>
                    <button
                      data-testid={`branch-delete-${b.code.toLowerCase()}`}
                      onClick={() => handleDelete(b)}
                      className="btn-chunky bg-white p-2 text-[#E63946]"
                      aria-label={`${b.name} şubesini sil`}
                    >
                      <Trash2 size={16} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
              ))}
              {branches.length === 0 && (
                <p className="text-sm font-medium text-[#4A4A4A]">Henüz şube yok.</p>
              )}
            </div>
          </div>
        </div>
        </>
        )}

        {tab === "jobs" && (
        <div className="mt-10" data-testid="job-applications-section">
          <div className="flex flex-wrap items-center gap-3">
            <input
              data-testid="panel-job-search"
              type="text"
              value={jobQuery}
              onChange={(e) => setJobQuery(e.target.value)}
              className="w-full max-w-sm border-2 border-[#1A1A1A] bg-white px-4 py-2.5 text-sm font-medium text-[#1A1A1A] outline-none focus:shadow-[3px_3px_0_0_#E63946]"
              placeholder="İsim, telefon veya not ara..."
            />
            <select
              data-testid="panel-job-position-filter"
              value={jobPos}
              onChange={(e) => setJobPos(e.target.value)}
              className="border-2 border-[#1A1A1A] bg-white px-4 py-2.5 text-sm font-bold text-[#1A1A1A] outline-none"
            >
              <option value="">Tüm Pozisyonlar</option>
              {jobPositions.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div className="mt-6 space-y-4" data-testid="job-applications-list">
            {filteredJobs.length === 0 && (
              <p className="text-sm font-medium text-[#4A4A4A]">
                {jobApps.length === 0 ? "Henüz iş başvurusu yok." : "Arama/filtreyle eşleşen başvuru bulunamadı."}
              </p>
            )}
            {filteredJobs.map((a) => (
              <div key={a.id} data-testid={`job-application-${a.id}`} className="card-print p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  {editApp?.kind === "job" && editApp.id === a.id ? (
                    <div className="flex-1 space-y-2">
                      <input data-testid="job-app-edit-name" value={appForm.name} onChange={(e) => setAppForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="Ad Soyad" />
                      <input data-testid="job-app-edit-phone" value={appForm.phone} onChange={(e) => setAppForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} placeholder="Telefon" />
                      <input data-testid="job-app-edit-position" value={appForm.position} onChange={(e) => setAppForm((f) => ({ ...f, position: e.target.value }))} className={inputCls} placeholder="Pozisyon" />
                      <div className="flex gap-2">
                        <button type="button" data-testid="job-app-edit-save" onClick={saveEditApp} className="btn-chunky bg-[#1A7A3D] px-4 py-2 text-xs uppercase tracking-wide text-white">
                          Kaydet
                        </button>
                        <button type="button" data-testid="job-app-edit-cancel" onClick={() => setEditApp(null)} className="btn-chunky bg-white px-4 py-2 text-xs uppercase tracking-wide text-[#1A1A1A]">
                          Vazgeç
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-base font-black uppercase tracking-tight text-[#1A1A1A]">
                        {a.name}
                        <span className="ml-3 text-sm font-bold text-[#4A4A4A]">{a.phone}</span>
                      </p>
                      <p className="mt-1 inline-block border border-[#1A1A1A] px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-[#1A1A1A]">
                        {a.position}
                      </p>
                      {!a.is_read && (
                        <span
                          data-testid={`job-app-new-badge-${a.id}`}
                          className="ml-2 inline-block border border-[#E63946] bg-[#E63946]/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-[#E63946]"
                        >
                          Yeni
                        </span>
                      )}
                      {a.note && (
                        <p className="mt-2 text-xs font-medium italic text-[#4A4A4A]">Not: {a.note}</p>
                      )}
                      {a.admin_note && (
                        <p className="mt-2 border-l-2 border-[#E8A13A] pl-2 text-xs font-bold text-[#1A1A1A]">Panel Notu: {a.admin_note}</p>
                      )}
                      <p className="mt-2 text-xs font-medium text-[#4A4A4A]">
                        {new Date(a.created_at).toLocaleString("tr-TR")}
                      </p>
                    </div>
                  )}
                  {appButtons("job", a)}
                </div>
                {noteEditor("job", a)}
              </div>
            ))}
          </div>
        </div>
        )}

        {tab === "franchise" && (
        <div className="mt-10" data-testid="franchise-applications-section">
          <div className="flex flex-wrap items-center gap-3">
            <input
              data-testid="panel-franchise-search"
              type="text"
              value={frQuery}
              onChange={(e) => setFrQuery(e.target.value)}
              className="w-full max-w-sm border-2 border-[#1A1A1A] bg-white px-4 py-2.5 text-sm font-medium text-[#1A1A1A] outline-none focus:shadow-[3px_3px_0_0_#E63946]"
              placeholder="İsim, telefon, e-posta veya şehir ara..."
            />
            <select
              data-testid="panel-franchise-city-filter"
              value={frCity}
              onChange={(e) => setFrCity(e.target.value)}
              className="border-2 border-[#1A1A1A] bg-white px-4 py-2.5 text-sm font-bold text-[#1A1A1A] outline-none"
            >
              <option value="">Tüm Şehirler</option>
              {frCities.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="mt-6 space-y-4" data-testid="franchise-applications-list">
            {filteredFr.length === 0 && (
              <p className="text-sm font-medium text-[#4A4A4A]">
                {franchiseApps.length === 0 ? "Henüz bayilik başvurusu yok." : "Arama/filtreyle eşleşen başvuru bulunamadı."}
              </p>
            )}
            {filteredFr.map((a) => (
              <div key={a.id} data-testid={`franchise-application-${a.id}`} className="card-print p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  {editApp?.kind === "franchise" && editApp.id === a.id ? (
                    <div className="flex-1 space-y-2">
                      <input data-testid="franchise-app-edit-name" value={appForm.name} onChange={(e) => setAppForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="Ad Soyad" />
                      <input data-testid="franchise-app-edit-phone" value={appForm.phone} onChange={(e) => setAppForm((f) => ({ ...f, phone: e.target.value }))} className={inputCls} placeholder="Telefon" />
                      <input data-testid="franchise-app-edit-email" value={appForm.email} onChange={(e) => setAppForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} placeholder="E-posta" />
                      <input data-testid="franchise-app-edit-city" value={appForm.city} onChange={(e) => setAppForm((f) => ({ ...f, city: e.target.value }))} className={inputCls} placeholder="Şehir" />
                      <div className="flex gap-2">
                        <button type="button" data-testid="franchise-app-edit-save" onClick={saveEditApp} className="btn-chunky bg-[#1A7A3D] px-4 py-2 text-xs uppercase tracking-wide text-white">
                          Kaydet
                        </button>
                        <button type="button" data-testid="franchise-app-edit-cancel" onClick={() => setEditApp(null)} className="btn-chunky bg-white px-4 py-2 text-xs uppercase tracking-wide text-[#1A1A1A]">
                          Vazgeç
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-base font-black uppercase tracking-tight text-[#1A1A1A]">
                        {a.name}
                        <span className="ml-3 text-sm font-bold text-[#4A4A4A]">{a.phone}</span>
                      </p>
                      <p className="mt-1 text-sm font-medium text-[#4A4A4A]">
                        {a.email} · {a.city}
                      </p>
                      {!a.is_read && (
                        <span
                          data-testid={`franchise-app-new-badge-${a.id}`}
                          className="mt-1 inline-block border border-[#E63946] bg-[#E63946]/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-[#E63946]"
                        >
                          Yeni
                        </span>
                      )}
                      {a.note && (
                        <p className="mt-2 text-xs font-medium italic text-[#4A4A4A]">Not: {a.note}</p>
                      )}
                      {a.admin_note && (
                        <p className="mt-2 border-l-2 border-[#E8A13A] pl-2 text-xs font-bold text-[#1A1A1A]">Panel Notu: {a.admin_note}</p>
                      )}
                      <p className="mt-2 text-xs font-medium text-[#4A4A4A]">
                        {new Date(a.created_at).toLocaleString("tr-TR")}
                      </p>
                    </div>
                  )}
                  {appButtons("franchise", a)}
                </div>
                {noteEditor("franchise", a)}
              </div>
            ))}
          </div>
        </div>
        )}
      </main>
    </div>
  );
}
